import * as React from 'react';
import {
  Box,
  Button,
  InputProps,
  UnorderedList,
  ListItem,
  useFormControl,
  useMultiStyleConfig,
  useToast,
  // Icon,
  // IconButton,
  Flex,
} from '@chakra-ui/react';
import {
  useDropzone,
  FileWithPath,
  Accept,
  FileRejection,
} from 'react-dropzone';
import adserver from 'services/adserver';
import { formatApiErrors } from 'helpers';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'hooks';
import { selectDataUploadLimit } from 'store/consts';
// import { FiTrash } from 'react-icons/fi';

type FileDataType = {
  id?: number;
  name?: string;
  uploadedFile?: string;
};

export type InputFileWithUploadType = {
  fileId?: {
    id?: number;
    fileName?: string;
    uploadedFile?: string;
  };
  accept?: Accept;
  minSize?: number;
  maxSize?: number;
  isDisabled?: boolean;
  onChange: (fileId?: number) => void;
  endpoint?: string;
};

/**
 * Komponent obsługujący upload plików na potrzeby kreacji
 * Plik jest wysyłany do endpointa API, zwracane jest id, które to staje się wartością pola formularza
 */
export const InputFileWithUpload = ({
  fileId,
  isDisabled,
  accept,
  onChange,
  size,
  variant,
  minSize,
  maxSize,
  endpoint = '/product/specification/',
  ...props
}: InputFileWithUploadType & InputProps): JSX.Element => {
  const { t } = useTranslation();
  const dataUploadLimit = useAppSelector(selectDataUploadLimit);
  const toast = useToast({
    position: 'bottom',
    status: 'error',
    isClosable: true,
  });
  const chakraInputProps = useFormControl(props);
  const chakraInputStyle = useMultiStyleConfig('InputFile', {
    size,
    variant,
  });
  const [fileData, setFileData] = React.useState<FileDataType | undefined>();
  /**
   * status uploadowania pliku: undefined dla idle lub cyfra 0-100 oznaczająca procent uploadu
   */
  const [uploadingProgress, setUploadingProgesss] = React.useState<
    number | undefined
  >();

  const handleAcceptedFiles = (acceptedFiles: FileWithPath[]) => {
    if (acceptedFiles.length) {
      uploadFile(acceptedFiles[0]);
    }
  };

  const handleRejectedFiles = (fileRejections: FileRejection[]) => {
    fileRejections.forEach(fileRejection => {
      toast({
        title: 'Plik odrzucony',
        description: (
          <UnorderedList>
            {fileRejection.errors.map(error => (
              <ListItem key={error.code}>{error.message}</ListItem>
            ))}
          </UnorderedList>
        ),
      });
    });
  };

  // const handleClearFile = () => {
  //   setFileData(undefined);
  //   if (onChange) onChange(undefined);
  // };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: handleAcceptedFiles,
    onDropRejected: handleRejectedFiles,
    maxFiles: 1,
    disabled: isDisabled,
    accept,
    minSize,
    maxSize: maxSize ?? (dataUploadLimit || undefined),
  });

  async function uploadFile(file: FileWithPath) {
    try {
      setFileData({
        name: file.name,
        uploadedFile: URL.createObjectURL(file),
      });

      const url = endpoint;
      const method = 'POST';
      const data = new FormData();
      data.append('uploadedFile', file);
      data.append('name', file.name);

      const result = await adserver({
        url,
        method,
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (status: { loaded: number; total: number }) =>
          setUploadingProgesss(
            Math.round((status.loaded * 100) / status.total),
          ),
      });

      setFileData(result.data);

      if (!onChange) return;
      onChange(result.data || undefined);
    } catch (error) {
      toast({
        title: 'Błąd podczas wysyłania pliku',
        description: formatApiErrors(error),
      });

      setFileData(undefined);

      if (onChange) onChange(undefined);
    }
    setUploadingProgesss(undefined);
  }

  React.useEffect(() => {
    if (fileId) {
      setFileData(fileId);
    } else {
      setFileData(undefined);
    }
  }, [fileId]);

  return (
    <Flex alignContent='stretch' alignItems='center' {...props}>
      <Box
        flex='auto'
        __css={chakraInputStyle.field}
        {...chakraInputProps}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Box __css={chakraInputStyle.heading}>{fileData?.name}</Box>
        <Box __css={chakraInputStyle.details}>
          {uploadingProgress && <Box>{uploadingProgress}%</Box>}
        </Box>
        <Button colorScheme='gray' size='sm' sx={chakraInputStyle.action}>
          {t('selectFile', 'Wybierz plik')}
        </Button>
      </Box>
    </Flex>
  );
};
